/**
 * @file     XPBar.tsx
 * @readme   ../README.md
 * @purpose  XP progress bar showing current level and progress to next level.
 */
'use client';

import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useAppTheme } from '@/core/theme';
import { useAcademy } from '../AcademyContext';
import { getLevelForXp, getLevelProgress } from '../xp';

export function XPBar() {
  const { theme } = useAppTheme();
  const dur = theme.motion.durations;
  const ease = theme.motion.easing;
  const { totalXp } = useAcademy();

  const level = getLevelForXp(totalXp);
  const { current, required, percent } = getLevelProgress(totalXp);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: theme.semanticColors.textPrimary }}>
          Level {level.level}: {level.label}
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color: theme.semanticColors.textMuted,
            transition: `all ${dur.normal}ms ${ease.standard}`,
            '@media (prefers-reduced-motion: no-preference)': {
              transition: `all ${dur.slow}ms ${ease.decelerate}`,
            },
          }}
        >
          {totalXp} XP{required > 0 ? ` / ${current}/${required} to next` : ''}
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={percent}
        aria-label={`Level ${level.level} progress: ${current} of ${required} XP`}
        sx={{
          height: 8,
          borderRadius: `${theme.layout.radius.pill}px`,
          backgroundColor: theme.semanticColors.borderSubtle,
          '& .MuiLinearProgress-bar': {
            borderRadius: `${theme.layout.radius.pill}px`,
            background: `linear-gradient(90deg, ${theme.semanticColors.interactivePrimary}, ${alpha(theme.rawSemanticColors.interactivePrimary, 0.7)})`,
            transition: `transform ${dur.slow}ms ${ease.decelerate}`,
          },
          '@media (prefers-reduced-motion: reduce)': {
            '& .MuiLinearProgress-bar': {
              transition: 'none',
            },
          },
        }}
      />
    </Box>
  );
}
